/**
 * Filterable Listing
 */
import { getViewportSize } from 'js/helpers.js';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger.js';
import { createFilledElement } from '../../../../../common/filters/fill-template.js';
const componentName = 'company-news';

class CompanyNews extends HTMLElement {

    /**
     * Component Setup
     */
    // Elements
    elements = {
        // Config
        data: this.querySelector(`.${componentName}__data`),
        filterForm: this.querySelector(`.${componentName}__form`),
        resultTemplate: this.querySelector(`.${componentName}__result-template`),
        // Old
        resultList: this.querySelector(`.${componentName}__results-list`),
        chevronRight: this.querySelector(`.${componentName}__chevron-right`),
        images: this.querySelectorAll(`.${componentName}__image`),
        imageContainer: this.querySelector(`.${componentName}__image-container`)
    };

    constants = Object.freeze({
        listingService: this.elements.data.dataset.listingService,
        imageSpeeds: [2, 6, 8, 12, 3],
        scrollSpeed: getViewportSize() === 'mobile' ? 10 : 50,
    });

    /**
     * Partial type definition of a result object
     * @typedef {{
     *     title: string?,
     *     description: string?,
     *     link: string?,
     *     image: string?,
     *     contentType: string?,
     *     date: string?,
     * }} ResultItem
     */

    /**
     * Execute Search
     * @param {filterParams}
     */
    runQuery = async () => {
        window.dispatchEvent(new CustomEvent('show-loading'));

        try {
            const searchParams = this.getSearchParams();
            const response = await fetch(
                `${this.constants.listingService}?${searchParams.toString()}`,
                { headers: { 'Cache-Control': 'max-age=3600' } }
            );
            if (!response.ok) {
                throw new Error();
            }

            /**
             * @type {{
             *     facetCounts: Record<string, Record<string, number>>
             *     loadMore: {
             *         currentPage: number,
             *         totalMatches: number,
             *         totalPages: number,
             *     },
             *     message: string,
             *     next: string,
             *     query: string,
             *     results: ResultItem[],
             *     success: boolean,
             * }}
             */
            const data = await response.json();
            if (!data || !data.success || !data.results) {
                throw new Error();
            }

            // Display results
            const resultElements = data.results.map((result) => this.createResultElement(result));
            this.elements.resultList.replaceChildren(...resultElements);

        } catch {
            console.error('company-news component failed to load.'); // eslint-disable-line no-console
        } finally {
            window.dispatchEvent(new CustomEvent('hide-loading'));
        }
    };

    /** @returns {URLSearchParams} */
    getSearchParams = () => {
        const formData = [...new FormData(this.elements.filterForm)]
            .filter(([, value]) => value !== '');
        return new URLSearchParams(formData);
    };

    /**
     * @param {ResultItem} resultItem
     * @returns {HTMLElement}
     */
    createResultElement = (resultItem) => createFilledElement(this.elements.resultTemplate, 'data-xt', {
        title: resultItem.title,
        link: resultItem.link
    });

    /**
     * Add scrolling animation to images
     */
    addScrollAnimation = () => {
        // Set up ScrollTrigger
        gsap.registerPlugin(ScrollTrigger);
        this.elements.images.forEach((image, index) => {
            const imageY = this.constants.imageSpeeds[index] * this.constants.scrollSpeed;
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: this.elements.imageContainer,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true
                }
            });
            const imageTween = gsap.fromTo(image, {
                y: ((imageY / 2) + (10 * (4 - index)))
            }, {
                y: (imageY / -2),
                ease: 'none'
            });
            tl.add(imageTween);
        });
    };

    hideMobileOverflow = () => {
        if (getViewportSize() === 'mobile') {
            const container = this.closest('.column-control__container');
            if (container) {
                container.style.overflow = 'hidden';
            }
        }
    };

    /**
     * Initialize Component
     */
    connectedCallback() {
        this.runQuery();
        this.addScrollAnimation();
        this.hideMobileOverflow();
    }
}

window.customElements.define(componentName, CompanyNews);
